import React, { useState } from "react";
import {
  Download,
  FileText,
  Eye,
  CircleX,
  InfoIcon,
  Upload,
  ShieldCheck,
  RotateCcw,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ErrorBoundary from "@/components/ErrorBoundary";
import FilePreviewModal from "@/components/FilePreview/FilePreviewModal";
import { generateDocumentName } from "@/features/documents/utils/generateDocumentName";
import { downloadFile } from "@/ajax/documents/downloadFile";
import { downloadAllFilesZip } from "@/ajax/documents/downloadAllFilesZip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import { FileType } from "@/features/files/types/file-type";
import Loading from "@/components/Loading";
import { TRPCClientError } from "@trpc/client";
import { UploadDocuments } from "@/components/UploadDocuments";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type CredentialEditRequest =
  RouterOutputs["credentialFiles"]["getCredentialEditAccess"]["request"];
const DEFAULT_CREDENTIAL_EDIT_REQUEST_MESSAGE =
  "Hi, could you please approve credential document edit access so our team can upload and update your credential documents?";

const DocumentRow = ({
  fileName,
  documentType,
  onView,
  onDownload,
  isDownloading,
}: {
  fileName: string;
  documentType?: string;
  onView: () => void;
  onDownload: () => void;
  isDownloading: boolean;
}) => (
  <div className="py-1">
    <div className="flex items-center justify-between">
      <button
        className="flex items-center gap-3 text-left"
        onClick={onView}
        aria-label={`View document: ${fileName}`}
      >
        <div className="text-blue-600">
          <FileText size={24} />
        </div>
        <div>
          <h3 className="text-gray-900 font-medium">{fileName}</h3>
          {documentType ? (
            <p className="text-gray-500 text-sm">{documentType}</p>
          ) : null}
        </div>
      </button>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={onView}
          aria-label={`View document: ${fileName}`}
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={onDownload}
          disabled={isDownloading}
          aria-label={`Download document: ${fileName}`}
        >
          {isDownloading ? (
            <div className="w-4 h-4 border-2 border-t-transparent border-blue-600 rounded-full animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  </div>
);

const DocumentsErrorState = ({
  message,
  containerClassName = "mt-6 bg-white",
  contentClassName = "p-4",
}: {
  message: string;
  containerClassName?: string;
  contentClassName?: string;
}) => (
  <div className={containerClassName}>
    <div className={contentClassName}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
      </div>
      <div className="py-2 text-center">
        <div className="flex items-center justify-center gap-2 text-gray-700">
          <CircleX className="h-5 w-5" />
          <span>{message}</span>
        </div>
      </div>
    </div>
  </div>
);

const DocumentsValid = ({
  userId,
  userName,
  editable,
  allowUploads = false,
  credentialEditRequest,
  onRequestCredentialEditAccess,
  isRequestingCredentialEditAccess = false,
  showCredentialAccessControls = false,
  organizationBusinessUnitId,
  containerClassName = "mt-6 bg-white",
  contentClassName = "p-4",
}: {
  userId: string;
  userName: string;
  editable: boolean;
  allowUploads?: boolean;
  credentialEditRequest?: CredentialEditRequest;
  onRequestCredentialEditAccess?: (message?: string) => void;
  isRequestingCredentialEditAccess?: boolean;
  showCredentialAccessControls?: boolean;
  organizationBusinessUnitId?: string;
  containerClassName?: string;
  contentClassName?: string;
}) => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [showUploader, setShowUploader] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [sendRequestAccessMessage, setSendRequestAccessMessage] =
    useState(true);
  const [requestAccessMessage, setRequestAccessMessage] = useState(
    DEFAULT_CREDENTIAL_EDIT_REQUEST_MESSAGE,
  );
  const [selectedDocument, setSelectedDocument] = useState<FileType | null>(
    null,
  );
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(
    null,
  );
  const filesQuery = useQuery(
    trpc.credentialFiles.getFilesCheckPermission.queryOptions({
      switchUserId: userId,
      organizationBusinessUnitId,
    }),
  );
  const accessRequestsQuery = useQuery(
    trpc.credentialFiles.listMyCredentialEditRequests.queryOptions(undefined, {
      enabled: showCredentialAccessControls,
    }),
  );
  const revokeAccessMutation = useMutation(
    trpc.credentialFiles.revokeCredentialEditAccess.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.credentialFiles.listMyCredentialEditRequests.queryOptions(),
          ),
          queryClient.invalidateQueries({
            queryKey: trpc.credentialFiles.getFilesCheckPermission.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.credentialFiles.getCredentialEditAccess.queryKey(),
          }),
        ]);
        toast.success("Access revoked");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to revoke access");
      },
    }),
  );

  const handleUploadComplete = () => {
    filesQuery.refetch();
  };

  if (filesQuery.error) {
    const code =
      filesQuery.error instanceof TRPCClientError
        ? filesQuery.error.data?.code
        : (filesQuery.error as { code?: string } | undefined)?.code;
    const message =
      code === "A0403"
        ? "The provider did not share documents"
        : "Failed to load documents";
    return (
      <DocumentsErrorState
        message={message}
        containerClassName={containerClassName}
        contentClassName={contentClassName}
      />
    );
  }

  if (filesQuery.isLoading) {
    return (
      <div className={containerClassName}>
        <div className={contentClassName}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
          </div>
          <div className="py-6 flex justify-center">
            <Loading className="h-12 w-12" />
          </div>
        </div>
      </div>
    );
  }

  const documents = filesQuery.data?.data ?? [];
  const hasPermission = filesQuery.data?.hasPermission ?? false;

  const handleDownload = async (file: FileType) => {
    setDownloadingFileId(file.id);
    try {
      const fileName = generateDocumentName(file);
      await downloadFile({
        userId,
        fileId: file.id,
        fileName,
        organizationBusinessUnitId,
      });
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDownloadAll = async () => {
    setDownloadLoading(true);
    try {
      await downloadAllFilesZip({
        userId,
        userName,
        files: allDocuments,
        organizationBusinessUnitId,
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const allDocuments = documents || [];
  const displayedDocuments = allDocuments;
  const documentsByType = displayedDocuments.reduce<Record<string, FileType[]>>(
    (groups, file) => {
      const typeName = (file.type_name || "Other documents").trim();
      if (!groups[typeName]) {
        groups[typeName] = [];
      }
      groups[typeName].push(file);
      return groups;
    },
    {},
  );
  const groupedDocuments = Object.entries(documentsByType).sort(
    ([typeA], [typeB]) => typeA.localeCompare(typeB),
  );
  const activeCredentialAccessRequests =
    accessRequestsQuery.data?.filter(
      (request) => request.status === "approved",
    ) ?? [];
  const requestPending = credentialEditRequest?.status === "pending";
  const showRequestEditAccess =
    !allowUploads &&
    Boolean(organizationBusinessUnitId && onRequestCredentialEditAccess);
  const handleSendRequest = () => {
    onRequestCredentialEditAccess?.(
      sendRequestAccessMessage && requestAccessMessage.trim()
        ? requestAccessMessage.trim()
        : undefined,
    );
    setRequestAccessDialogOpen(false);
    setSendRequestAccessMessage(true);
    setRequestAccessMessage(DEFAULT_CREDENTIAL_EDIT_REQUEST_MESSAGE);
  };

  return (
    <div className={containerClassName}>
      <div className={contentClassName}>
        {/* Header Section */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Documents</h2>
          <div className="flex flex-wrap justify-end gap-2">
            {showCredentialAccessControls && (
              <Button
                variant="outline"
                className="font-medium"
                onClick={() => setAccessDialogOpen(true)}
              >
                <ShieldCheck className="w-4 h-4 mr-1" />
                {accessRequestsQuery.isLoading
                  ? "Loading access..."
                  : `${activeCredentialAccessRequests.length} org${activeCredentialAccessRequests.length === 1 ? "" : "s"} with edit access`}
              </Button>
            )}
            {showRequestEditAccess && (
              <Button
                variant="outline"
                className="font-medium"
                onClick={() => setRequestAccessDialogOpen(true)}
                disabled={isRequestingCredentialEditAccess || requestPending}
              >
                <Upload className="w-4 h-4 mr-1" />
                {requestPending
                  ? "Edit Access Requested"
                  : isRequestingCredentialEditAccess
                    ? "Requesting..."
                    : "Request Edit Access"}
              </Button>
            )}
            {allowUploads && (
              <Button
                variant="outline"
                className="font-medium"
                onClick={() => setShowUploader((prev) => !prev)}
              >
                <Upload className="w-4 h-4 mr-1" />
                {showUploader ? "Hide Upload" : "Upload Documents"}
              </Button>
            )}
            {allDocuments.length > 0 && (
              <Button
                variant="ghost"
                className="text-blue-600 font-medium"
                onClick={handleDownloadAll}
                disabled={downloadLoading}
              >
                <Download className="w-4 h-4 mr-1" />
                {downloadLoading ? "Zipping files..." : "Download All"}
              </Button>
            )}
          </div>
        </div>
        {allowUploads && showUploader && (
          <div className="mb-4">
            <UploadDocuments
              autoUpload={true}
              minDocuments={1}
              user={{ id: userId }}
              switchUserId={userId}
              organizationBusinessUnitId={organizationBusinessUnitId}
              onFilesUploaded={handleUploadComplete}
            />
          </div>
        )}
        {!hasPermission && (
          <div className="py-2 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <span>The provider did not share their documents</span>
              <Popover>
                <PopoverTrigger>
                  <InfoIcon className="h-4 w-4 text-gray-700 cursor-help" />
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2">
                  <p className="text-sm text-gray-600">
                    Providers have the option to control who can view their
                    documents. If you believe this is a mistake, please reach
                    out to the provider to have them reshare access.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}
        {hasPermission && allDocuments.length > 0 && (
          <div>
            <div className="space-y-6">
              {groupedDocuments.map(([typeLabel, docs]) => (
                <div key={typeLabel}>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span>{typeLabel}</span>
                  </div>
                  <div className="mt-2 divide-y divide-gray-100">
                    {docs.map((doc) => (
                      <DocumentRow
                        key={doc.id}
                        fileName={doc.generated_title || doc.file_name}
                        onView={() => setSelectedDocument(doc)}
                        onDownload={() => handleDownload(doc)}
                        isDownloading={downloadingFileId === doc.id}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {selectedDocument && (
              <FilePreviewModal
                userId={userId}
                file={selectedDocument}
                onClose={() => setSelectedDocument(null)}
                editable={editable}
                organizationBusinessUnitId={organizationBusinessUnitId}
              />
            )}
          </div>
        )}
        {hasPermission && allDocuments.length === 0 && (
          <div className="py-2 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-700">
              <FileText className="h-5 w-5" />
              <span>No credentialling documents have been uploaded yet</span>
            </div>
          </div>
        )}
        {showCredentialAccessControls && (
          <Dialog open={accessDialogOpen} onOpenChange={setAccessDialogOpen}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  Organizations with credential edit access
                </DialogTitle>
              </DialogHeader>
              {accessRequestsQuery.isLoading ? (
                <div className="py-6 flex justify-center">
                  <Loading className="h-10 w-10" />
                </div>
              ) : activeCredentialAccessRequests.length === 0 ? (
                <p className="text-sm text-gray-600">
                  No organizations currently have edit access to your credential
                  documents.
                </p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {activeCredentialAccessRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between gap-3 py-3"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {request.organizationBusinessUnitName}
                        </p>
                        <p className="text-xs text-gray-500">
                          Approved{" "}
                          {request.respondedAt
                            ? new Date(request.respondedAt).toLocaleDateString()
                            : new Date(
                                request.requestedAt,
                              ).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={revokeAccessMutation.isPending}
                        onClick={() =>
                          revokeAccessMutation.mutate({
                            requestId: request.id,
                          })
                        }
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
        {showRequestEditAccess && (
          <Dialog
            open={requestAccessDialogOpen}
            onOpenChange={setRequestAccessDialogOpen}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900">
                  Request credential edit access
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Request permission to upload and update this provider's
                  credential documents.
                </DialogDescription>
              </DialogHeader>
              <div className="mb-1">
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox
                    id="send-credential-edit-request-message"
                    checked={sendRequestAccessMessage}
                    onCheckedChange={(checked) =>
                      setSendRequestAccessMessage(checked === true)
                    }
                  />
                  <Label
                    htmlFor="send-credential-edit-request-message"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Send a chat message to the provider
                  </Label>
                </div>
                {sendRequestAccessMessage && (
                  <div className="space-y-1.5">
                    <Textarea
                      value={requestAccessMessage}
                      onChange={(event) =>
                        setRequestAccessMessage(event.target.value)
                      }
                      placeholder="Write a message to the provider..."
                      maxLength={1000}
                      className="text-sm"
                      rows={5}
                    />
                    <div className="text-xs text-gray-500 text-right">
                      {requestAccessMessage.length}/1000
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="sm:space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRequestAccessDialogOpen(false)}
                  disabled={isRequestingCredentialEditAccess}
                  className="border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSendRequest}
                  disabled={isRequestingCredentialEditAccess}
                >
                  {isRequestingCredentialEditAccess ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send request"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export const DocumentsCard = ({
  userId,
  userName,
  editable,
  allowUploads = false,
  credentialEditRequest,
  onRequestCredentialEditAccess,
  isRequestingCredentialEditAccess = false,
  showCredentialAccessControls = false,
  organizationBusinessUnitId,
  containerClassName = "mt-6 bg-white",
  contentClassName = "p-4",
}: {
  userId: string;
  userName: string;
  editable: boolean;
  allowUploads?: boolean;
  credentialEditRequest?: CredentialEditRequest;
  onRequestCredentialEditAccess?: (message?: string) => void;
  isRequestingCredentialEditAccess?: boolean;
  showCredentialAccessControls?: boolean;
  organizationBusinessUnitId?: string;
  containerClassName?: string;
  contentClassName?: string;
}) => {
  const ErrorComponent = ({ error }: { error: Error }) => {
    const trpcError = error instanceof TRPCClientError ? error : null;
    const code =
      trpcError?.data?.code ?? (error as { code?: string } | undefined)?.code;
    const message =
      code === "A0403"
        ? "The provider did not share documents"
        : "Failed to load documents";
    return (
      <DocumentsErrorState
        message={message}
        containerClassName={containerClassName}
        contentClassName={contentClassName}
      />
    );
  };
  return (
    <ErrorBoundary component={ErrorComponent}>
      <DocumentsValid
        userId={userId}
        userName={userName}
        editable={editable}
        allowUploads={allowUploads}
        credentialEditRequest={credentialEditRequest}
        onRequestCredentialEditAccess={onRequestCredentialEditAccess}
        isRequestingCredentialEditAccess={isRequestingCredentialEditAccess}
        showCredentialAccessControls={showCredentialAccessControls}
        organizationBusinessUnitId={organizationBusinessUnitId}
        containerClassName={containerClassName}
        contentClassName={contentClassName}
      />
    </ErrorBoundary>
  );
};

export default DocumentsCard;
